/*!
 * Offsure collection filters v4 — left accordion rail (desktop) mirrored
 * into a dialog.js bottom sheet (mobile). Client-side filtering over
 * already-rendered cards (this store's `collection.filters` only exposes
 * Availability and Price — see reports/redesign-2026-09-03/CATALOG-SPEC.md).
 * v4 adds: density toggle (?density=3|4, localStorage fallback), applied
 * filter chips with enter/exit motion, a fully keyboard-operable sort
 * listbox (aria-activedescendant), disabled zero-count rows, a live
 * "Show N styles" sheet button and "Filter (n)" trigger, and a measured
 * sticky offset (--odr-top) taken from the Horizon header.
 * No reload; real URL sync via history.pushState/popstate.
 */
(function () {
  if (window.__offsureCollectionFiltersReady) return;
  window.__offsureCollectionFiltersReady = true;

  // Older menu/bookmarked links used Shopify's native filter parameter.
  // Reload with our catalog parameter so Clear all restores the full assortment,
  // even when Shopify has already narrowed the server-rendered collection.
  var entryURL = new URL(window.location.href);
  var legacyColors = entryURL.searchParams.getAll('filter.v.option.color');
  if (legacyColors.length) {
    if (!entryURL.searchParams.has('color')) entryURL.searchParams.set('color', legacyColors.join(','));
    entryURL.searchParams.delete('filter.v.option.color');
    entryURL.searchParams.delete('page');
    window.location.replace(entryURL.href);
    return;
  }


  var PRICE_BANDS = [
    { value: 'u80', label: 'Under $80', test: function (p) { return p < 80; } },
    { value: 'o80', label: '$80 and over', test: function (p) { return p >= 80; } }
  ];
  var KIND_LABEL = { piece: 'Style', color: 'Color', size: 'Size', price: 'Price' };
  var DENSITY_KEY = 'odr-density';

  function reducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  // ---------------- sticky offset from the real header ----------------
  function measureStickyTop(root) {
    var sticky = document.querySelector('[id^="ohd-sections"]') || document.querySelector('#header-group [style*="sticky"]');
    var h = 63;
    if (sticky) {
      var cs = getComputedStyle(sticky);
      if (cs.position === 'sticky' || cs.position === 'fixed') h = Math.round(sticky.getBoundingClientRect().height) || 63;
    }
    root.style.setProperty('--odr-top', h + 'px');
  }

  function initBar(bar) {
    if (bar.__odrInit) return;
    bar.__odrInit = true;

    var gridId = bar.getAttribute('data-grid-target');
    var grid = document.getElementById(gridId);
    if (!grid) return;
    var sectionEl = bar.closest('[id^="odr-"]') || document.body;
    measureStickyTop(sectionEl);
    window.addEventListener('resize', function () { measureStickyTop(sectionEl); });

    // ---------------- toolbar "is-stuck" state (tint only once engaged) ----------------
    (function initStuckToolbar() {
      var toolbarEl = bar.querySelector('.odr-toolbar');
      if (!toolbarEl) return;
      if ('IntersectionObserver' in window) {
        var sentinel = document.createElement('div');
        sentinel.setAttribute('aria-hidden', 'true');
        sentinel.style.cssText = 'position:relative;height:1px;';
        toolbarEl.parentNode.insertBefore(sentinel, toolbarEl);
        var topOffset = parseInt(getComputedStyle(sectionEl).getPropertyValue('--odr-top')) || 63;
        var io = new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            toolbarEl.classList.toggle('is-stuck', !entry.isIntersecting);
          });
        }, { rootMargin: '-' + (topOffset + 1) + 'px 0px 0px 0px', threshold: 0 });
        io.observe(sentinel);
      } else {
        var onScroll = function () {
          var r = toolbarEl.getBoundingClientRect();
          var topOffset2 = parseInt(getComputedStyle(sectionEl).getPropertyValue('--odr-top')) || 63;
          toolbarEl.classList.toggle('is-stuck', r.top <= topOffset2);
        };
        window.addEventListener('scroll', onScroll, { passive: true });
        onScroll();
      }
    })();

    var cards = Array.prototype.slice.call(grid.querySelectorAll('.odr-card[data-odr-piece]'));

    // Unfiltered label counts distinct product styles + distinct colors.
    var stylesSeen = {};
    var colorsSeen = {};
    cards.forEach(function (card) {
      var pieceVal = card.getAttribute('data-odr-piece') || '';
      var colorVals = (card.getAttribute('data-odr-color') || '').split('|').filter(Boolean);
      if (pieceVal) stylesSeen[pieceVal] = true;
      colorVals.forEach(function (c) { colorsSeen[c] = true; });
    });
    var styleCount = Object.keys(stylesSeen).length;
    var colorCount = Object.keys(colorsSeen).length;
    var unfilteredLabel = styleCount + (styleCount === 1 ? ' style' : ' styles') +
      ' · ' + cards.length + (cards.length === 1 ? ' colorway' : ' colorways');

    var sectionRoot = document;
    var optionEls = Array.prototype.slice.call(sectionRoot.querySelectorAll('[data-odr-filter]'));
    var sortEls = Array.prototype.slice.call(sectionRoot.querySelectorAll('[data-odr-sort-opt]'));
    var clearBtns = Array.prototype.slice.call(sectionRoot.querySelectorAll('[data-odr-clearall]'));
    var railClearBtn = sectionRoot.querySelector('.odr-filterpanel__clearall[data-odr-clearall]');
    var countEls = Array.prototype.slice.call(sectionRoot.querySelectorAll('[data-odr-count]'));
    var mobileBtn = sectionRoot.querySelector('[data-odr-mobile-filter-btn]');
    var filterNEl = sectionRoot.querySelector('[data-odr-filter-n]');
    var sheetApply = sectionRoot.querySelector('[data-odr-sheet-apply]');
    var accordions = Array.prototype.slice.call(sectionRoot.querySelectorAll('[data-odr-accordion]'));
    var sortWrap = sectionRoot.querySelector('[data-odr-sort-wrap]');
    var sortLabel = sectionRoot.querySelector('[data-odr-sort-label]');
    var chipsEl = sectionRoot.querySelector('[data-odr-chips]');
    var densityBtns = Array.prototype.slice.call(sectionRoot.querySelectorAll('[data-odr-density]'));

    var labelByKindVal = {};
    optionEls.forEach(function (el) {
      var kind = el.getAttribute('data-odr-filter');
      var val = el.getAttribute('data-value');
      var row = el.closest('.odr-fcheck, .odr-sizepill');
      var lab = row && row.querySelector('.odr-fcheck__label, .odr-sizepill__label');
      if (!labelByKindVal[kind]) labelByKindVal[kind] = {};
      labelByKindVal[kind][val] = lab ? lab.textContent.trim() : val;
    });

    var state = { piece: [], color: [], size: [], price: [], sort: '', density: '' };

    // ---------------- accordions ----------------
    accordions.forEach(function (acc) {
      var trigger = acc.querySelector('[data-odr-acc-trigger]');
      if (!trigger) return;
      trigger.addEventListener('click', function () {
        var open = acc.getAttribute('data-open') === 'true';
        acc.setAttribute('data-open', open ? 'false' : 'true');
        trigger.setAttribute('aria-expanded', open ? 'false' : 'true');
      });
    });

    // ---------------- density ----------------
    function readDensity() {
      var params = new URLSearchParams(window.location.search);
      var q = params.get('density');
      if (q === '3' || q === '4') return q;
      try {
        var l = window.localStorage.getItem(DENSITY_KEY);
        if (l === '3' || l === '4') return l;
      } catch (err) {}
      return bar.getAttribute('data-default-density') || '3';
    }
    function applyDensity() {
      bar.setAttribute('data-density', state.density);
      densityBtns.forEach(function (b) {
        b.setAttribute('aria-pressed', b.getAttribute('data-odr-density') === state.density ? 'true' : 'false');
      });
      var sizes = state.density === '4' ? '(min-width: 901px) 20vw, 46vw' : '(min-width: 901px) 26vw, 46vw';
      grid.querySelectorAll('img[data-opc-img-a], img[data-opc-img-b]').forEach(function (img) {
        if (img.getAttribute('srcset')) img.setAttribute('sizes', sizes);
      });
    }
    densityBtns.forEach(function (b) {
      b.addEventListener('click', function () {
        var d = b.getAttribute('data-odr-density');
        if (d === state.density) return;
        state.density = d;
        try { window.localStorage.setItem(DENSITY_KEY, d); } catch (err) {}
        applyDensity();
        writeURL(true);
      });
    });

    // ---------------- sort listbox (keyboard: Arrow/Home/End/Enter/Space/Escape/Tab) ----------------
    var sortTrigger = sortWrap && sortWrap.querySelector('[data-odr-trigger]');
    var sortPanel = sortWrap && sortWrap.querySelector('[data-odr-panel]');
    var activeIdx = -1;

    function setActive(i) {
      activeIdx = Math.max(0, Math.min(sortEls.length - 1, i));
      sortEls.forEach(function (el, j) { el.setAttribute('data-active', j === activeIdx ? 'true' : 'false'); });
      if (sortPanel) sortPanel.setAttribute('aria-activedescendant', sortEls[activeIdx].id);
    }
    function openSort() {
      if (!sortPanel || !sortPanel.hidden) return;
      sortPanel.hidden = false;
      sortWrap.setAttribute('data-open', 'true');
      sortTrigger.setAttribute('aria-expanded', 'true');
      var cur = 0;
      sortEls.forEach(function (el, j) { if (el.getAttribute('aria-selected') === 'true') cur = j; });
      setActive(cur);
      sortPanel.focus({ preventScroll: true });
    }
    function closeSort(returnFocus) {
      if (!sortPanel || sortPanel.hidden) return;
      sortPanel.hidden = true;
      sortWrap.setAttribute('data-open', 'false');
      sortTrigger.setAttribute('aria-expanded', 'false');
      sortPanel.removeAttribute('aria-activedescendant');
      if (returnFocus) sortTrigger.focus({ preventScroll: true });
    }
    function selectSort(el) {
      state.sort = el.getAttribute('data-odr-sort-opt') || '';
      apply(true);
      closeSort(true);
    }
    if (sortTrigger && sortPanel) {
      sortTrigger.addEventListener('click', function (e) {
        e.stopPropagation();
        if (sortPanel.hidden) openSort(); else closeSort(false);
      });
      sortTrigger.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') { e.preventDefault(); openSort(); }
      });
      sortPanel.addEventListener('keydown', function (e) {
        switch (e.key) {
          case 'ArrowDown': e.preventDefault(); setActive(activeIdx + 1); break;
          case 'ArrowUp': e.preventDefault(); setActive(activeIdx - 1); break;
          case 'Home': e.preventDefault(); setActive(0); break;
          case 'End': e.preventDefault(); setActive(sortEls.length - 1); break;
          case 'Enter': case ' ': e.preventDefault(); if (activeIdx > -1) selectSort(sortEls[activeIdx]); break;
          case 'Escape': e.preventDefault(); e.stopPropagation(); closeSort(true); break;
          case 'Tab': closeSort(false); break;
        }
      });
      sortEls.forEach(function (el, j) {
        el.addEventListener('mousemove', function () { if (activeIdx !== j) setActive(j); });
        el.addEventListener('click', function () { selectSort(el); });
      });
      document.addEventListener('click', function (e) {
        if (!sortWrap.contains(e.target)) closeSort(false);
      });
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && !sortPanel.hidden) closeSort(true);
      });
    }

    // ---------------- URL <-> state ----------------
    function readStateFromURL() {
      var params = new URLSearchParams(window.location.search);
      state.piece = (params.get('piece') || '').split(',').filter(Boolean);
      state.color = (params.get('color') || '').split(',').filter(Boolean);
      state.size = (params.get('size') || '').split(',').filter(Boolean);
      state.price = (params.get('price') || '').split(',').filter(Boolean);
      state.sort = params.get('sort') || '';
      state.density = readDensity();
    }

    function writeURL(push) {
      var params = new URLSearchParams(window.location.search);
      ['piece', 'color', 'size', 'price'].forEach(function (kind) {
        if (state[kind].length) params.set(kind, state[kind].join(','));
        else params.delete(kind);
      });
      if (state.sort) params.set('sort', state.sort); else params.delete('sort');
      if (state.density && state.density !== (bar.getAttribute('data-default-density') || '3')) params.set('density', state.density);
      else params.delete('density');
      var qs = params.toString();
      var url = window.location.pathname + (qs ? '?' + qs : '');
      if (url === window.location.pathname + window.location.search) return;
      if (push) history.pushState({ odrFilters: true }, '', url);
      else history.replaceState({ odrFilters: true }, '', url);
    }

    function priceBandOf(price) {
      for (var i = 0; i < PRICE_BANDS.length; i++) {
        if (PRICE_BANDS[i].test(price)) return PRICE_BANDS[i].value;
      }
      return null;
    }

    function activeFilterCount() {
      return state.piece.length + state.color.length + state.size.length + state.price.length;
    }

    function syncControlsFromState() {
      optionEls.forEach(function (el) {
        var kind = el.getAttribute('data-odr-filter');
        var val = el.getAttribute('data-value');
        var active = state[kind].indexOf(val) > -1;
        if (el.tagName === 'INPUT') el.checked = active;
        else el.setAttribute('aria-selected', active ? 'true' : 'false');
      });
      sortEls.forEach(function (el) {
        var active = el.getAttribute('data-odr-sort-opt') === state.sort;
        el.setAttribute('aria-selected', active ? 'true' : 'false');
        if (active && sortLabel) sortLabel.textContent = el.textContent.trim();
      });
      var anyActive = !!(activeFilterCount() || state.sort);
      clearBtns.forEach(function (b) { b.hidden = !anyActive; });
      var n = activeFilterCount();
      if (mobileBtn) mobileBtn.setAttribute('data-active', n > 0 ? 'true' : 'false');
      if (filterNEl) filterNEl.textContent = n > 0 ? '(' + n + ')' : '';
    }

    function setRowDisabled(el, disabled) {
      var row = el.closest('.odr-fcheck, .odr-sizepill');
      if (row) row.setAttribute('data-zero', disabled ? 'true' : 'false');
      if (el.tagName === 'INPUT') {
        el.disabled = disabled;
        if (disabled) el.setAttribute('aria-disabled', 'true'); else el.removeAttribute('aria-disabled');
      }
    }

    function updateFacetCounts() {
      ['piece', 'color', 'size'].forEach(function (kind) {
        var seen = {};
        cards.forEach(function (card) {
          var raw = (kind === 'size' || kind === 'color')
            ? (card.getAttribute('data-odr-' + (kind === 'size' ? 'sizes' : kind)) || '').split('|').filter(Boolean)
            : [card.getAttribute('data-odr-' + kind) || ''];
          raw.forEach(function (val) {
            if (!val) return;
            if (!seen[val]) seen[val] = 0;
            if (cardMatches(card, kind)) seen[val]++;
          });
        });
        sectionRoot.querySelectorAll('[data-odr-filter="' + kind + '"]').forEach(function (el) {
          var val = el.getAttribute('data-value');
          var row = el.closest('.odr-fcheck, .odr-sizepill');
          var countEl = row && row.querySelector('[data-odr-fcount]');
          var n = seen[val] || 0;
          if (countEl) countEl.textContent = n;
          setRowDisabled(el, n === 0 && state[kind].indexOf(val) === -1);
        });
      });
      var priceSeen = {};
      cards.forEach(function (card) {
        var price = parseFloat(card.getAttribute('data-odr-price') || '0');
        var band = priceBandOf(price);
        if (!band) return;
        if (cardMatches(card, 'price')) priceSeen[band] = (priceSeen[band] || 0) + 1;
      });
      sectionRoot.querySelectorAll('[data-odr-filter="price"]').forEach(function (el) {
        var val = el.getAttribute('data-value');
        var row = el.closest('.odr-fcheck');
        var countEl = row && row.querySelector('[data-odr-fcount]');
        var n = priceSeen[val] || 0;
        if (countEl) countEl.textContent = n;
        setRowDisabled(el, n === 0 && state.price.indexOf(val) === -1);
      });
    }

    function cardMatches(card, ignoreKind, overrideState) {
      var st = overrideState || state;
      var piece = card.getAttribute('data-odr-piece') || '';
      var colors = (card.getAttribute('data-odr-color') || '').split('|').filter(Boolean);
      var sizes = (card.getAttribute('data-odr-sizes') || '').split('|').filter(Boolean);
      var price = parseFloat(card.getAttribute('data-odr-price') || '0');
      var band = priceBandOf(price);
      if (ignoreKind !== 'piece' && st.piece.length && st.piece.indexOf(piece) === -1) return false;
      if (ignoreKind !== 'color' && st.color.length && !st.color.some(function (c) { return colors.indexOf(c) > -1; })) return false;
      if (ignoreKind !== 'size' && st.size.length && !st.size.some(function (s) { return sizes.indexOf(s) > -1; })) return false;
      if (ignoreKind !== 'price' && st.price.length && st.price.indexOf(band) === -1) return false;
      return true;
    }

    function applySort(visible) {
      if (!state.sort) {
        visible.forEach(function (card, i) { card.style.order = i; });
        return;
      }
      var withKey = visible.map(function (card, i) {
        var price = parseFloat(card.getAttribute('data-odr-price') || '0');
        var piece = (card.getAttribute('data-odr-piece') || '').toLowerCase();
        return { card: card, price: price, piece: piece, i: i };
      });
      withKey.sort(function (a, b) {
        if (state.sort === 'price-asc') return a.price - b.price || a.i - b.i;
        if (state.sort === 'price-desc') return b.price - a.price || a.i - b.i;
        if (state.sort === 'alpha-asc') return a.piece.localeCompare(b.piece) || a.i - b.i;
        return a.i - b.i;
      });
      withKey.forEach(function (entry, i) { entry.card.style.order = i; });
    }

    // ---------------- applied chips (diffed so enter/exit can animate) ----------------
    function chipLabel(kind, val) {
      if (kind === 'price') {
        for (var i = 0; i < PRICE_BANDS.length; i++) if (PRICE_BANDS[i].value === val) return PRICE_BANDS[i].label;
      }
      return (labelByKindVal[kind] && labelByKindVal[kind][val]) || val;
    }
    function renderChips() {
      if (!chipsEl) return;
      var wanted = [];
      ['piece', 'color', 'size', 'price'].forEach(function (kind) {
        state[kind].forEach(function (val) { wanted.push({ key: kind + '::' + val, kind: kind, val: val }); });
      });
      var existing = Array.prototype.slice.call(chipsEl.querySelectorAll('.odr-chip'));
      var keep = {};
      wanted.forEach(function (w) { keep[w.key] = true; });
      existing.forEach(function (chip) {
        if (keep[chip.getAttribute('data-key')] || chip.hasAttribute('data-exit')) return;
        chip.setAttribute('data-exit', '');
        chip.querySelector('button').disabled = true;
        var done = function () { if (chip.parentNode) chip.parentNode.removeChild(chip); syncClear(); };
        if (reducedMotion()) done(); else window.setTimeout(done, 130);
      });
      var have = {};
      existing.forEach(function (chip) { if (!chip.hasAttribute('data-exit')) have[chip.getAttribute('data-key')] = true; });
      var clearBtn = chipsEl.querySelector('.odr-chips__clear');
      wanted.forEach(function (w) {
        if (have[w.key]) return;
        var chip = document.createElement('span');
        chip.className = 'odr-chip';
        chip.setAttribute('data-key', w.key);
        chip.innerHTML = '<span class="odr-chip__kind">' + KIND_LABEL[w.kind] + '</span><span class="odr-chip__label"></span>' +
          '<button type="button" class="odr-chip__x"><svg viewBox="0 0 12 12" width="10" height="10" aria-hidden="true"><path d="M2 2l8 8M10 2l-8 8" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg></button>';
        chip.querySelector('.odr-chip__label').textContent = chipLabel(w.kind, w.val);
        var x = chip.querySelector('button');
        x.setAttribute('aria-label', 'Remove ' + chipLabel(w.kind, w.val));
        x.addEventListener('click', function () {
          var idx = state[w.kind].indexOf(w.val);
          if (idx > -1) state[w.kind].splice(idx, 1);
          apply(true);
        });
        if (!reducedMotion()) chip.setAttribute('data-enter', '');
        chipsEl.insertBefore(chip, clearBtn || null);
        if (!reducedMotion()) {
          // Two frames so the initial state is committed before the transition runs.
          requestAnimationFrame(function () { requestAnimationFrame(function () { chip.removeAttribute('data-enter'); }); });
        }
      });
      if (!clearBtn) {
        clearBtn = document.createElement('button');
        clearBtn.type = 'button';
        clearBtn.className = 'odr-chips__clear';
        clearBtn.textContent = 'Clear all';
        clearBtn.addEventListener('click', function () { clearAll(); });
        chipsEl.appendChild(clearBtn);
      }
      syncClear();
    }
    function syncClear() {
      if (!chipsEl) return;
      var live = chipsEl.querySelectorAll('.odr-chip:not([data-exit])').length;
      var clearBtn = chipsEl.querySelector('.odr-chips__clear');
      if (clearBtn) clearBtn.hidden = live < 2;
      chipsEl.hidden = chipsEl.querySelectorAll('.odr-chip').length === 0;
      // Only one "Clear all" should be visible at a time. The chip row's own
      // Clear all (shown once 2+ chips are live) wins; the rail's copy is a
      // fallback for when chips aren't visible (0-1 filters, or sort-only).
      if (railClearBtn && !railClearBtn.hidden && clearBtn && !clearBtn.hidden) {
        railClearBtn.hidden = true;
      }
    }

    // Facet drop order when nothing matches: price and size are the least
    // selective (broadest bands / most shared across styles), so they're
    // dropped before color or piece when hunting for a nearest-match set.
    var NEAREST_DROP_ORDER = ['price', 'size', 'color', 'piece'];
    function nearestMatches() {
      for (var i = 0; i < NEAREST_DROP_ORDER.length; i++) {
        var dropKind = NEAREST_DROP_ORDER[i];
        if (!state[dropKind].length) continue;
        var trial = { piece: state.piece, color: state.color, size: state.size, price: state.price };
        trial[dropKind] = [];
        var matches = cards.filter(function (card) { return cardMatches(card, null, trial); });
        if (matches.length) return { cards: matches.slice(0, 4), dropped: dropKind };
      }
      return { cards: [], dropped: null };
    }

    var emptyRow = null;
    function ensureEmptyRow() {
      if (emptyRow) return emptyRow;
      emptyRow = document.createElement('div');
      emptyRow.className = 'odr-empty-filtered';
      grid.appendChild(emptyRow);
      return emptyRow;
    }

    // Nearest-match cards render as real siblings inside .odr-grid (not a
    // nested grid) so they inherit the exact same --odr-cols/--colgap/
    // --rowgap geometry as a normal result set (Fable 2026-09-10: a nested
    // 4-col grid inside the message box put a lone card off the shared
    // column rhythm).
    var nearestEyebrow = null;
    var nearestCardEls = [];
    function clearNearest() {
      nearestCardEls.forEach(function (el) { if (el.parentNode) el.parentNode.removeChild(el); });
      nearestCardEls = [];
      if (nearestEyebrow && nearestEyebrow.parentNode) nearestEyebrow.parentNode.removeChild(nearestEyebrow);
      nearestEyebrow = null;
    }
    function renderEmptyRow(row) {
      clearNearest();
      var near = nearestMatches();
      var actionsHtml = '<div class="odr-empty-filtered__actions">' +
        '<a href="#" data-odr-clearall-link>Clear all</a>';
      if (state.color.length) {
        actionsHtml += '<span class="odr-empty-filtered__sep">·</span><a href="#" data-odr-remove-color>Remove colour filter</a>';
      }
      actionsHtml += '</div>';
      row.innerHTML = '<p>No colorways match those filters.</p>' + actionsHtml;
      row.querySelector('[data-odr-clearall-link]').addEventListener('click', function (e) {
        e.preventDefault();
        clearAll();
      });
      var removeColorBtn = row.querySelector('[data-odr-remove-color]');
      if (removeColorBtn) {
        removeColorBtn.addEventListener('click', function (e) {
          e.preventDefault();
          state.color = [];
          apply(true);
        });
      }
      if (near.cards.length) {
        nearestEyebrow = document.createElement('p');
        nearestEyebrow.className = 'odr-empty-filtered__eyebrow';
        nearestEyebrow.textContent = 'Closest matches';
        grid.appendChild(nearestEyebrow);
        near.cards.forEach(function (card) {
          var clone = card.cloneNode(true);
          clone.removeAttribute('data-odr-hidden');
          clone.style.order = '';
          grid.appendChild(clone);
          nearestCardEls.push(clone);
        });
      }
    }

    function apply(push) {
      grid.setAttribute('data-odr-loading', 'true');
      var visible = [];
      // Restore each colorway's identity before matching filters. Shopper swatch
      // swaps must not leave a Navy card displaying White Beach after Clear all.
      cards.forEach(function (card) {
        var ownColor = card.getAttribute('data-odr-color');
        var meta = card.querySelector('[data-opc-meta]');
        if (meta && meta.textContent.trim() !== ownColor) {
          var ownSwatch = Array.prototype.find.call(card.querySelectorAll('.opc__body [data-opc-swatch]'), function (swatch) {
            return swatch.getAttribute('data-color-name') === ownColor;
          });
          if (ownSwatch) ownSwatch.click();
        }
      });
      cards.forEach(function (card) {
        var match = cardMatches(card, null);
        card.setAttribute('data-odr-hidden', match ? 'false' : 'true');
        if (match) {
          visible.push(card);
          // Show a selected color, not an unrelated editorial starting color.
          if (state.color.length) {
            var swatches = Array.prototype.slice.call(card.querySelectorAll('.opc__body [data-opc-swatch]'));
            var selectedMatches = swatches.some(function (swatch) {
              return swatch.getAttribute('aria-pressed') === 'true' && state.color.indexOf(swatch.getAttribute('data-color-name')) !== -1;
            });
            if (!selectedMatches) {
              var matchingSwatch = swatches.find(function (swatch) {
                return state.color.indexOf(swatch.getAttribute('data-color-name')) !== -1;
              });
              if (matchingSwatch) matchingSwatch.click();
            }
          }
        }
      });
      applySort(visible);
      var row = ensureEmptyRow();
      row.hidden = visible.length !== 0;
      if (visible.length === 0) renderEmptyRow(row);
      else clearNearest();
      var anyFilter = activeFilterCount() > 0;
      var n = visible.length;
      countEls.forEach(function (countEl) {
        countEl.textContent = anyFilter ? n + (n === 1 ? ' colorway' : ' colorways') : unfilteredLabel;
      });
      if (sheetApply) {
        sheetApply.textContent = n === 0 ? 'No colorways match' : 'Show ' + n + (n === 1 ? ' colorway' : ' colorways');
      }
      syncControlsFromState();
      updateFacetCounts();
      renderChips();
      applyDensity();
      window.setTimeout(function () { grid.removeAttribute('data-odr-loading'); }, 260);
      writeURL(push);
    }

    function clearAll() {
      state = { piece: [], color: [], size: [], price: [], sort: state.sort, density: state.density };
      apply(true);
    }

    optionEls.forEach(function (el) {
      var evt = el.tagName === 'INPUT' ? 'change' : 'click';
      el.addEventListener(evt, function () {
        var kind = el.getAttribute('data-odr-filter');
        var val = el.getAttribute('data-value');
        var idx = state[kind].indexOf(val);
        if (idx > -1) state[kind].splice(idx, 1); else state[kind].push(val);
        apply(true);
      });
    });
    clearBtns.forEach(function (b) { b.addEventListener('click', function () { clearAll(); }); });

    window.addEventListener('popstate', function () {
      readStateFromURL();
      apply(false);
    });

    readStateFromURL();
    apply(false);
  }

  function boot() {
    document.querySelectorAll('[data-odr-filterbar]').forEach(initBar);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  document.addEventListener('shopify:section:load', boot);
})();
